import { alphaAdminUi } from "./alphaAdminUi";
import { Mail, MessageSquare } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "../../contexts/ToastContext";
import notificationContactsService from "../../services/notificationContactsService";
import type {
  CommunicationEndpoint,
  CommunicationEndpointPair,
  NotificationCommunicationEndpointType,
  NotificationEndpointVerificationSource,
} from "../../types/adminNotifications";
import {
  NOTIFICATION_EMAIL_MAX_LENGTH,
  describeNotificationError,
  isValidE164,
  isValidNotificationEmail,
} from "../../utils/adminNotificationForm";
import { SMS_CONTACT_EDITING_ENABLED } from "../../utils/notificationPreferencesMatrix";
import ConfirmationModal from "../ConfirmationModal";

interface NotificationEndpointsFormProps {
  appearance?: "default" | "alpha";
  /** Explicit target user; every request targets this path ID. */
  userId: string;
  /** Login email, shown so admins do not confuse it with the notification email. */
  loginEmail: string;
  endpoints: CommunicationEndpointPair;
  /** Receives the email/SMS pair after every successful mutation. */
  onChange: (next: CommunicationEndpointPair) => void;
  /** Refetch hook used after an ambiguous outcome. */
  onRefresh?: () => void;
}

type Busy = "email" | "sms" | "remove" | "verify-email" | "verify-sms" | null;

const VERIFICATION_SOURCE_LABELS: Record<NotificationEndpointVerificationSource, string> = {
  USER_CODE: "by code",
  LOGIN_EMAIL: "from the login email",
  ADMIN: "by an administrator",
};

const inputClass =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 transition-all placeholder:text-gray-400 focus:border-rp-amber focus:outline-none focus:ring-1 focus:ring-rp-amber dark:border-rp-separator dark:bg-rp-surface-raised dark:text-white dark:placeholder:text-gray-500";
const primaryButtonClass =
  "rounded-lg bg-rp-amber px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-yellow-600 disabled:opacity-50";
const secondaryButtonClass =
  "rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:border-gray-300 disabled:opacity-50 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600";
const dangerButtonClass =
  "rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:bg-transparent dark:text-red-400 dark:hover:bg-red-900/20";

const formatUtc = (iso: string): string =>
  new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
    timeZoneName: "short",
  });

const VerifiedPill: React.FC<{ endpoint: CommunicationEndpoint; appearance?: "default" | "alpha" }> = ({ endpoint, appearance = "default" }) =>
  endpoint.verifiedAtUtc ? (
    <span
      title={`Verified ${formatUtc(endpoint.verifiedAtUtc)}${
        endpoint.verificationSource ? ` ${VERIFICATION_SOURCE_LABELS[endpoint.verificationSource]}` : ""
      }`}
      className={appearance === "alpha" ? "rounded-v7-control border border-v7-success/30 bg-v7-success/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-v7-success" : "rounded border border-green-200 bg-green-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-500"}
    >
      Verified
    </span>
  ) : (
    <span className={appearance === "alpha" ? "rounded-v7-control border border-v7-ink/15 bg-v7-paper px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-v7-ink-soft" : "rounded border border-gray-200 bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-600 dark:border-gray-800 dark:bg-rp-surface-raised dark:text-gray-400"}>
      Unverified
    </span>
  );

/**
 * Shared notification email / SMS editor used by the Users sidebar card and the
 * Notifications > Users edit modal. Owns only the transient edit state; the
 * current endpoints and the target user come from the host.
 */
const NotificationEndpointsForm: React.FC<NotificationEndpointsFormProps> = ({
  appearance = "default",
  userId,
  loginEmail,
  endpoints,
  onChange,
  onRefresh,
}) => {
  const toast = useToast();
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailDraft, setEmailDraft] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [editingSms, setEditingSms] = useState(false);
  const [smsDraft, setSmsDraft] = useState("");
  const [smsError, setSmsError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [busy, setBusy] = useState<Busy>(null);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // A new target user must never inherit another user's half-typed values.
  useEffect(() => {
    setEditingEmail(false);
    setEmailDraft("");
    setEmailError(null);
    setEditingSms(false);
    setSmsDraft("");
    setSmsError(null);
    setConfirmRemove(false);
  }, [userId]);

  const failureMessage = useCallback(
    (error: unknown): string => {
      const described = describeNotificationError(error);
      if (described.kind === "not-found") return "User not found in the notification service.";
      if (described.ambiguous) {
        return onRefresh
          ? "Could not confirm the change. Refresh to check the current value."
          : "Could not confirm the change. Reload to check the current value.";
      }
      return described.message;
    },
    [onRefresh]
  );

  const saveEmail = async (): Promise<void> => {
    const value = emailDraft.trim();
    if (!isValidNotificationEmail(value)) {
      setEmailError(`Enter a valid email address (max ${NOTIFICATION_EMAIL_MAX_LENGTH} characters).`);
      return;
    }
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setBusy("email");
    setEmailError(null);
    try {
      const saved = await notificationContactsService.replaceUserEmail(userId, value);
      if (!mountedRef.current) return;
      onChange({ email: saved, sms: endpoints.sms });
      setEditingEmail(false);
      setEmailDraft("");
      toast.success("Notification email updated.");
    } catch (error) {
      if (!mountedRef.current) return;
      setEmailError(failureMessage(error));
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setBusy(null);
    }
  };

  const saveSms = async (): Promise<void> => {
    const value = smsDraft.trim();
    if (!isValidE164(value)) {
      setSmsError("Enter an E.164 number, e.g. +15555550123.");
      return;
    }
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setBusy("sms");
    setSmsError(null);
    try {
      const saved = await notificationContactsService.replaceUserSms(userId, value);
      if (!mountedRef.current) return;
      onChange({ email: endpoints.email, sms: saved });
      setEditingSms(false);
      setSmsDraft("");
      toast.success("SMS number updated.");
    } catch (error) {
      if (!mountedRef.current) return;
      setSmsError(failureMessage(error));
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setBusy(null);
    }
  };

  const removeSms = async (): Promise<void> => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setBusy("remove");
    setSmsError(null);
    try {
      await notificationContactsService.removeUserSms(userId);
      if (!mountedRef.current) return;
      onChange({ email: endpoints.email, sms: null });
      setConfirmRemove(false);
      toast.success("SMS number removed.");
    } catch (error) {
      if (!mountedRef.current) return;
      setConfirmRemove(false);
      setSmsError(failureMessage(error));
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setBusy(null);
    }
  };

  const disabled = busy !== null;

  /** ADMIN attestation (PWA-2698): flips verification without sending a code. */
  const setVerified = async (endpointType: NotificationCommunicationEndpointType, verified: boolean): Promise<void> => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const isEmail = endpointType === "EMAIL";
    setBusy(isEmail ? "verify-email" : "verify-sms");
    if (isEmail) setEmailError(null);
    else setSmsError(null);
    try {
      const saved = await notificationContactsService.setUserEndpointVerified(userId, endpointType, verified);
      if (!mountedRef.current) return;
      onChange(isEmail ? { email: saved, sms: endpoints.sms } : { email: endpoints.email, sms: saved });
      toast.success(`${isEmail ? "Notification email" : "SMS number"} marked ${verified ? "verified" : "unverified"}.`);
    } catch (error) {
      if (!mountedRef.current) return;
      if (isEmail) setEmailError(failureMessage(error));
      else setSmsError(failureMessage(error));
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setBusy(null);
    }
  };

  const verifyButton = (endpointType: NotificationCommunicationEndpointType, endpoint: CommunicationEndpoint) => {
    const verified = endpoint.verifiedAtUtc !== null;
    const busyKey: Busy = endpointType === "EMAIL" ? "verify-email" : "verify-sms";
    return (
      <button
        type="button"
        onClick={() => setVerified(endpointType, !verified)}
        disabled={disabled}
        className={appearance === "alpha" ? alphaAdminUi.secondary : secondaryButtonClass}
      >
        {busy === busyKey ? "Saving..." : verified ? "Mark unverified" : "Mark verified"}
      </button>
    );
  };

  return (
    <div className="space-y-4">
      {/* Notification email */}
      <div>
        <div className="flex items-center gap-2">
          <Mail className={appearance === "alpha" ? "h-4 w-4 text-v7-ink/60" : "h-4 w-4 text-gray-500 dark:text-gray-400"} />
          <span className={appearance === "alpha" ? "text-[10px] font-semibold uppercase tracking-wider text-v7-ink/60" : "text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"}>
            Notification email
          </span>
        </div>
        <p className={appearance === "alpha" ? "mt-0.5 text-xs text-v7-ink/60" : "mt-0.5 text-xs text-gray-500 dark:text-gray-400"}>
          Separate from the login email <span className="font-mono">{loginEmail}</span>.
        </p>
        {editingEmail ? (
          <div className="mt-2 space-y-2">
            <input
              type="email"
              value={emailDraft}
              maxLength={NOTIFICATION_EMAIL_MAX_LENGTH}
              onChange={event => setEmailDraft(event.target.value)}
              placeholder="notifications@example.com"
              aria-label="Notification email"
              disabled={disabled}
              className={appearance === "alpha" ? alphaAdminUi.input : inputClass}
            />
            <div className="flex gap-2">
              <button type="button" onClick={saveEmail} disabled={disabled} className={appearance === "alpha" ? alphaAdminUi.primary : primaryButtonClass}>
                {busy === "email" ? "Saving..." : "Save email"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditingEmail(false);
                  setEmailDraft("");
                  setEmailError(null);
                }}
                disabled={disabled}
                className={appearance === "alpha" ? alphaAdminUi.secondary : secondaryButtonClass}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {endpoints.email ? (
              <>
                <span className={appearance === "alpha" ? "text-sm text-v7-ink" : "text-sm text-gray-900 dark:text-white"}>{endpoints.email.value}</span>
                <VerifiedPill appearance={appearance} endpoint={endpoints.email} />
              </>
            ) : (
              <span className={appearance === "alpha" ? "text-sm italic text-v7-ink/60" : "text-sm italic text-gray-500 dark:text-gray-400"}>Not set</span>
            )}
            <button
              type="button"
              onClick={() => {
                setEmailDraft(endpoints.email?.value ?? "");
                setEmailError(null);
                setEditingEmail(true);
              }}
              disabled={disabled}
              className={appearance === "alpha" ? alphaAdminUi.secondary : secondaryButtonClass}
            >
              {endpoints.email ? "Edit" : "Set email"}
            </button>
            {endpoints.email && verifyButton("EMAIL", endpoints.email)}
          </div>
        )}
        {emailError && <p className={appearance === "alpha" ? "mt-1 text-xs text-v7-rust" : "mt-1 text-xs text-red-700 dark:text-red-400"}>{emailError}</p>}
      </div>

      {/* SMS */}
      <div>
        <div className="flex items-center gap-2">
          <MessageSquare className={appearance === "alpha" ? "h-4 w-4 text-v7-ink/60" : "h-4 w-4 text-gray-500 dark:text-gray-400"} />
          <span className={appearance === "alpha" ? "text-[10px] font-semibold uppercase tracking-wider text-v7-ink/60" : "text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"}>
            SMS
          </span>
        </div>
        {/* SMS is read-only for admins too until SMS notifications launch (owner decision 2026-09-11). */}
        {!SMS_CONTACT_EDITING_ENABLED ? (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {endpoints.sms ? (
              <>
                <span className={appearance === "alpha" ? "font-mono text-sm text-v7-ink" : "font-mono text-sm text-gray-900 dark:text-white"}>{endpoints.sms.value}</span>
                <VerifiedPill appearance={appearance} endpoint={endpoints.sms} />
              </>
            ) : (
              <span className={appearance === "alpha" ? "text-sm italic text-v7-ink/60" : "text-sm italic text-gray-500 dark:text-gray-400"}>Not set</span>
            )}
            <span className={appearance === "alpha" ? "text-xs text-v7-ink/60" : "text-xs text-gray-500 dark:text-gray-400"}>
              Editing is disabled until SMS notifications are enabled.
            </span>
          </div>
        ) : editingSms ? (
          <div className="mt-2 space-y-2">
            <input
              type="tel"
              value={smsDraft}
              onChange={event => setSmsDraft(event.target.value)}
              placeholder="+15555550123"
              aria-label="SMS number"
              disabled={disabled}
              className={appearance === "alpha" ? alphaAdminUi.input : inputClass}
            />
            <p className={appearance === "alpha" ? "text-xs text-v7-ink/60" : "text-xs text-gray-500 dark:text-gray-400"}>E.164 format, e.g. +15555550123.</p>
            <div className="flex gap-2">
              <button type="button" onClick={saveSms} disabled={disabled} className={appearance === "alpha" ? alphaAdminUi.primary : primaryButtonClass}>
                {busy === "sms" ? "Saving..." : "Save SMS"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditingSms(false);
                  setSmsDraft("");
                  setSmsError(null);
                }}
                disabled={disabled}
                className={appearance === "alpha" ? alphaAdminUi.secondary : secondaryButtonClass}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {endpoints.sms ? (
              <>
                <span className={appearance === "alpha" ? "font-mono text-sm text-v7-ink" : "font-mono text-sm text-gray-900 dark:text-white"}>{endpoints.sms.value}</span>
                <VerifiedPill appearance={appearance} endpoint={endpoints.sms} />
              </>
            ) : (
              <span className={appearance === "alpha" ? "text-sm italic text-v7-ink/60" : "text-sm italic text-gray-500 dark:text-gray-400"}>Not set</span>
            )}
            <button
              type="button"
              onClick={() => {
                setSmsDraft(endpoints.sms?.value ?? "");
                setSmsError(null);
                setEditingSms(true);
              }}
              disabled={disabled}
              className={appearance === "alpha" ? alphaAdminUi.secondary : secondaryButtonClass}
            >
              {endpoints.sms ? "Replace" : "Set SMS"}
            </button>
            {endpoints.sms && verifyButton("SMS", endpoints.sms)}
            {endpoints.sms && (
              <button
                type="button"
                onClick={() => setConfirmRemove(true)}
                disabled={disabled}
                className={appearance === "alpha" ? alphaAdminUi.danger : dangerButtonClass}
              >
                Remove
              </button>
            )}
          </div>
        )}
        {smsError && <p className={appearance === "alpha" ? "mt-1 text-xs text-v7-rust" : "mt-1 text-xs text-red-700 dark:text-red-400"}>{smsError}</p>}
      </div>

      {onRefresh && (
        <button
          type="button"
          onClick={onRefresh}
          disabled={disabled}
          className={appearance === "alpha" ? "text-xs font-semibold text-v7-bronze-dark hover:underline disabled:opacity-50" : "text-xs font-semibold text-rp-amber hover:underline disabled:opacity-50"}
        >
          Refresh
        </button>
      )}

      <ConfirmationModal appearance={appearance}
        isOpen={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={removeSms}
        loading={busy === "remove"}
        isDestructive
        confirmText="Remove SMS"
        title="Remove SMS number?"
        message={
          <span>
            SMS notifications for this user will stop until a new number is set. The login email is not
            affected.
          </span>
        }
      />
    </div>
  );
};

export default NotificationEndpointsForm;
